import React, { MutableRefObject, useEffect, useState, useCallback, useRef, ChangeEvent, useMemo } from 'react';
import JourneySlideover from 'src/common/JourneySlideOver';
import delay from 'lodash/delay';
import { Content } from 'src/common/interfaces/content.interface';
import { apiGetAllContents } from 'src/utils/journeyApi';
import { useCurrentOrganization } from 'src/store/organization';
import { Nullable } from 'src/types/nullable.type';
import { Pagination } from 'src/common/Pagination';
import isNull from 'lodash/isNull';
import debounce from 'lodash/debounce';
import { TextInput } from 'src/common/form/text-input';
import { DataTable } from '../../table/data-table';
import { generateColumn } from '../../table/LegacyTable';
import { IconButton } from 'src/common/icon-button';
import { SearchStrongIcon } from 'src/monet/icons';
import { WizardContentSectionLibraryProps } from './section/library';
import { LibraryHoverableCardSmall } from '../../library/hoverable-content-card-small';
import LoadingSpinner from '../../loading-spinner';

interface Props extends WizardContentSectionLibraryProps {
  openRef: MutableRefObject<() => void>;
  closeRef: MutableRefObject<() => void>;
}

const SLIDE_ANIMATION_TIMEOUT_IN_MS = 200;
const DEBOUNCE_DURATION_TO_PERFORM_SEARCH = 600;

export const WizardContentLibrarySlideOver = ({ openRef, closeRef, onSelectContent }: Props) => {
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [showSlideOver, setShowSlideOver] = useState(false);

  const searchInputElementRef = useRef<Nullable<HTMLInputElement>>(null);
  const currentOrganizationId = useCurrentOrganization((state) => state.currentOrganization.id);

  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const contentsRef = useRef<Nullable<Content[]>>(null);
  const contentsPageRef = useRef<{ page: number; pages: number }>({ page: 1, pages: 1 });

  const onSlideOverClosed = () => {
    setShowSlideOver(false);
    setSearchQuery('');
    delay(() => {
      setShowAddPanel(false);
    }, SLIDE_ANIMATION_TIMEOUT_IN_MS);
  };

  const fetchContentList = useCallback(
    (searchStr: string = '') => {
      setLoading(true);
      apiGetAllContents(currentOrganizationId, {
        page: contentsPageRef.current.page,
        ...(searchStr && { search_str: searchStr }),
      }).then((response) => {
        contentsRef.current = response.contents;
        contentsPageRef.current = { page: response.page, pages: response.pages };
        setLoading(false);
        searchInputElementRef.current?.focus();
      });
    },
    [currentOrganizationId]
  );

  useEffect(() => {
    openRef.current = () => {
      setShowAddPanel(true);
      fetchContentList();
      delay(() => {
        setShowSlideOver(true);
      }, SLIDE_ANIMATION_TIMEOUT_IN_MS);
    };

    closeRef.current = onSlideOverClosed;
  }, []);

  const debouncedSearchQueryHandler = useMemo(
    () => debounce((value) => fetchContentList(value), DEBOUNCE_DURATION_TO_PERFORM_SEARCH),
    [fetchContentList]
  );
  // Stop the invocation of the debounced function after unmounting
  useEffect(() => {
    return () => {
      debouncedSearchQueryHandler.cancel();
    };
  }, [debouncedSearchQueryHandler]);

  const columns = useMemo(
    () => [
      generateColumn({
        Header: () => null,
        accessor: 'name',
        width: '100%',
        Cell: ({ row }: { row: any }) => (
          <div className='relative cursor-pointer group' style={{ wordBreak: 'break-word' }}>
            <LibraryHoverableCardSmall
              previewSize='small'
              content={row.original}
              titleContainerClassName='pr-6'
              editable={false}
            />
            <div className='absolute right-2 top-2'>
              <div className='transition-opacity opacity-0 group-hover:opacity-100'>
                <IconButton>
                  <svg
                    width='20'
                    height='20'
                    className='text-bedrock-black w-5 h-5'
                    viewBox='0 0 20 20'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      fillRule='evenodd'
                      clipRule='evenodd'
                      d='M10.4996 5.71436C10.4996 5.43821 10.2757 5.21436 9.99958 5.21436C9.72344 5.21436 9.49958 5.43821 9.49958 5.71436V9.50007H5.71387C5.43772 9.50007 5.21387 9.72393 5.21387 10.0001C5.21387 10.2762 5.43772 10.5001 5.71387 10.5001H9.49958V14.2858C9.49958 14.5619 9.72344 14.7858 9.99958 14.7858C10.2757 14.7858 10.4996 14.5619 10.4996 14.2858V10.5001H14.2853C14.5614 10.5001 14.7853 10.2762 14.7853 10.0001C14.7853 9.72393 14.5614 9.50007 14.2853 9.50007H10.4996V5.71436Z'
                      fill='currentColor'
                    />
                  </svg>
                </IconButton>
              </div>
            </div>
          </div>
        ),
      }),
    ],
    []
  );

  if (!showAddPanel) {
    return null;
  }

  const onActivePageChanged = (e: unknown, pageNum: number) => {
    contentsPageRef.current.page = pageNum;
    setLoading(true);
    fetchContentList();
  };

  const { current: contentList } = contentsRef;
  const { current: contentPage } = contentsPageRef;

  return (
    <JourneySlideover
      isBedrock
      title='Pick from library'
      open={showSlideOver}
      maxWidth='auto'
      containerClassName='pointer-events-none z-50'
      panelContainerClassName='pointer-events-auto'
      panelClassName='w-[368px]'
      titleClassName='font-brand-serif'
      onClose={onSlideOverClosed}
    >
      <TextInput
        id='pick-content-search'
        ref={searchInputElementRef}
        inputProps={{
          type: 'text',
          value: searchQuery,
          placeholder: 'Search for content',
        }}
        disabled={loading}
        trailingAddon={
          <IconButton>
            <SearchStrongIcon />
          </IconButton>
        }
        hideOptional
        branded
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          setSearchQuery(value);
          debouncedSearchQueryHandler(value);
        }}
      />
      <div className='flex flex-col text-bedrock-black gap-2 h-full'>
        {loading ? (
          <LoadingSpinner containerClassName='h-full' />
        ) : (
          <>
            <div className='flex flex-col'>
              <div className='mt-4 sticky top-0 bg-bedrock-white z-50'>
                <span className='relative text-bedrock-p-strong after:absolute after:h-px after:bg-bedrock-white after:-bottom-[5px] after:left-0 after:-right-[400px]'>
                  All content
                </span>
              </div>
              {!isNull(contentList) && (
                <div className='flex flex-col gap-2'>
                  {contentList.length === 0 ? (
                    <span className='text-bedrock-p text-bedrock-black'>No content found</span>
                  ) : (
                    <DataTable
                      tableHeaderClassName='hide'
                      tableClassName='mt-px'
                      disableRowCursorPointer
                      onRowClick={(row: any) => {
                        onSelectContent(row.original);
                      }}
                      columns={columns}
                      data={contentList}
                    />
                  )}
                </div>
              )}
              {contentPage.pages > 1 && (
                <Pagination
                  size='small'
                  containerClassName='justify-center'
                  count={contentPage.pages}
                  page={contentPage.page}
                  onChange={onActivePageChanged}
                />
              )}
            </div>
          </>
        )}
      </div>
    </JourneySlideover>
  );
};
